import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { SurveyQuestionTargetInfo as SurveyTargetInfo } from "./surveyTargetListSlice";

export interface SurveyTargetState {
    state: LoadStateInfo;
    request?: RetrieveSurveyTargetRequest;
    selected?: SurveyTargetInfo;
}

const initialState: SurveyTargetState = {
    state: notStarted(),
};

export interface RetrieveSurveyTargetRequest {
    targetId: string;
}

export const retrieveSurveyTarget = createAsyncThunk(
    "retrieveSurveyQuestionTarget",
    async (param: RetrieveSurveyTargetRequest, thunkAPI) => {
        console.debug("retrieve survey target");

        const response = await fetch(`${getBackendUrl()}/api/survey/target/get/${param.targetId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyTargetRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.surveyTarget;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveSurveyQuestionTarget skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const surveyTargetSlice = createSlice({
    name: "surveyQuestionTarget",
    initialState,
    reducers: {
        select: (state, action: PayloadAction<SurveyTargetInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.target}`);
            state.selected = action.payload;
        },
        check: (state, action: PayloadAction<string | undefined>) => {
            if (state.state.state != LoadState.NotStarted
                && (action.payload == undefined || state.request?.targetId != action.payload)
            ) {
                state.request = undefined;
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        clean: (state) => {
            state.request = undefined;
            state.selected = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSurveyTarget.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveSurveyTarget.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveSurveyTarget.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const { select, check, clean } = surveyTargetSlice.actions;

export const selectSurveyTarget = (state: RootState) => state.surveyTarget;

export default surveyTargetSlice.reducer;
